import { FormProvider, useForm, useWatch } from 'react-hook-form';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
} from '@mui/material';

import {
  IIncomeStructureFormValues,
  IIncomeCategoryFormValues,
} from '@Router/NatureOfBusiness/Containers/NatureOfBusinessFormProvider/types';
import FormAutoCompleteWithIcon from '@Components/Form/FormAutoCompleteWithIcon';
import { useNatureOfBusinessForm } from '@Router/NatureOfBusiness/Containers/NatureOfBusinessFormProvider';
import {
  OTHER_INCOME_STRUCTURE_ID,
  INCOME_SUBGROUP_CATEGORY,
} from 'src/Enum/IncomeStructure';
import { useIncomeStructure } from '../../IncomeStructureProvider/IncomeStructureProvider';
import {
  createNewExtendedExportIncome,
  ExportIncomeModalFormValue,
  mapCountryToFormValues,
  updateExportIncomeCategory,
  getOptions,
} from './utils';
import { setEmptyYearFields } from '../../utils';

type ExportIncomeModalProps = {
  open: boolean;
  mode: 'create' | 'edit';
  close: () => void;
  keyItem?: string;
  editData?: IIncomeCategoryFormValues;
};

const ExportIncomeModal = ({
  open,
  mode,
  close,
  keyItem,
  editData,
}: ExportIncomeModalProps) => {
  const { countries } = useIncomeStructure();
  const initialValue =
    mode === 'edit' ? mapCountryToFormValues(editData, countries) : undefined;

  const exportIncomeModalContext = useForm<ExportIncomeModalFormValue>({
    defaultValues: initialValue,
    mode: 'onChange',
  });

  const { handleSubmit, reset, getValues, watch } = exportIncomeModalContext;
  const { control, setValue } = useNatureOfBusinessForm();

  const incomeStructure = useWatch({
    control,
    name: 'incomeStructure',
  }) as IIncomeStructureFormValues;
  const exportIncomeItems = incomeStructure?.exportIncomeItems || {};
  const exportIncomePcts = incomeStructure?.exportIncomePercentages || {};

  const currentCountry = watch('country');
  const options = getOptions(
    exportIncomeItems,
    countries,
    currentCountry,
    mode
  );

  const handleClose = () => {
    reset();
    close();
  };

  const handleCreateExportIncome = (formValues: ExportIncomeModalFormValue) => {
    const prevExtendedItems = Object.values(exportIncomeItems).filter(
      (item) =>
        item.incomeCategory?.subGroup === INCOME_SUBGROUP_CATEGORY.EXPORT_INCOME
    );
    const nextSequence =
      prevExtendedItems.reduce(
        (max, item) => Math.max(max, item.incomeCategory?.sequence ?? 0),
        0
      ) + 1;
    const newItemKey = `${INCOME_SUBGROUP_CATEGORY.EXPORT_INCOME}-${nextSequence}`;
    const newExtendedItem = createNewExtendedExportIncome(
      formValues,
      nextSequence
    );
    const exportIncomeOtherItem =
      exportIncomeItems[OTHER_INCOME_STRUCTURE_ID.OTHER_EXPORT_INCOME];

    setValue(
      `incomeStructure.exportIncomeItems.${newItemKey}`,
      newExtendedItem
    );
    setValue(
      `incomeStructure.exportIncomePercentages.${newItemKey}`,
      newExtendedItem
    );
    setValue(
      `incomeStructure.exportIncomeItems.${OTHER_INCOME_STRUCTURE_ID.OTHER_EXPORT_INCOME}`,
      setEmptyYearFields(exportIncomeOtherItem)
    );
  };

  const handleEditExportIncome = (
    formValues: ExportIncomeModalFormValue,
    keyItem: string
  ) => {
    const prevExtendedItem = exportIncomeItems[keyItem].incomeCategory;
    const prevExtendedPct = exportIncomePcts[keyItem].incomeCategory;

    const updateExtendedItem = updateExportIncomeCategory(
      formValues,
      prevExtendedItem
    );
    const updateExtendedPct = updateExportIncomeCategory(
      formValues,
      prevExtendedPct
    );

    setValue(
      `incomeStructure.exportIncomeItems.${keyItem}.incomeCategory`,
      updateExtendedItem
    );
    setValue(
      `incomeStructure.exportIncomePercentages.${keyItem}.incomeCategory`,
      updateExtendedPct
    );
  };

  const handleSave = () => {
    const formValues = getValues();

    if (mode === 'edit' && keyItem) {
      handleEditExportIncome(formValues, keyItem);
    } else {
      handleCreateExportIncome(formValues);
    }

    handleClose();
  };

  return (
    <FormProvider {...exportIncomeModalContext}>
      <Dialog open={open} maxWidth="md">
        <DialogTitle fontWeight={800} fontSize={16}>
          {mode === 'create' ? 'เพิ่ม' : 'แก้ไข'}
          รายการประเทศ
        </DialogTitle>
        <DialogContent>
          <FormAutoCompleteWithIcon
            name="country"
            options={options}
            value={currentCountry}
            getOptionLabel={(country) => country.nameTh}
            isOptionEqualToValue={(option, value) =>
              value ? option.nameTh === value.nameTh : false
            }
            placeholder="ค้นหา"
            label="กรุณาเลือกประเทศ"
            required
            disableClearable
          />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button color="inherit" onClick={handleClose}>
            ยกเลิก
          </Button>
          <Button onClick={handleSubmit(handleSave)} disabled={!currentCountry}>
            ยืนยัน
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

export default ExportIncomeModal;
